import { useCallback, useMemo, useState } from 'react';
import type { NavStack, Route } from './types';

/**
 * Hook to create a navigation stack.
 * @param initialRoutes - The initial routes to set in the stack.
 * @returns A NavStack object with the following methods:
 * - pop(): Removes the last route from the stack and returns it.
 * - push(route: Route): Adds a new route to the stack.
 * - replace(route: Route): Replaces the last route in the stack with a new one.
 * - reset(routes: Route[]): Resets the stack to a new set of routes.
 * - currentRoute: The current route at the top of the stack.
 */
export function useNavStack<RouteName = string>(
  initialRoutes: Route<RouteName>[] = [],
): NavStack<RouteName> {
  const [stack, setStack] = useState<Route<RouteName>[]>(initialRoutes);

  const pop = useCallback((): Route<RouteName> | undefined => {
    if (stack.length === 0) {
      return undefined;
    }
    let stackCopy = [...stack];
    const route = stackCopy.pop();
    setStack(stackCopy);
    return route;
  }, [stack]);

  const push = useCallback(
    (route: Route<RouteName>) => {
      let stackCopy = [...stack];
      stackCopy.push(route);
      setStack(stackCopy);
    },
    [stack],
  );

  const replace = useCallback(
    (route: Route<RouteName>) => {
      if (stack.length === 0) {
        return;
      }
      let stackCopy = [...stack];
      stackCopy[stackCopy.length - 1] = route;
      setStack(stackCopy);
    },
    [stack],
  );

  const reset = useCallback((routes: Route<RouteName>[] = []) => {
    setStack([...routes]);
  }, []);

  return useMemo<NavStack<RouteName>>(
    () => ({ pop, push, replace, reset, currentRoute: stack[stack.length - 1] }),
    [pop, push, replace, reset, stack],
  );
}
